// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonVariants } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color, xMask, xScreen } from "@keller/XDL/Utilities";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    subtitle = "subtitle",
    itemsList = "itemsList",
    buttonList = "buttonList",
}

interface OwnProps {
    fields: {
        [fields.title]: boolean;
        [fields.subtitle]: boolean;
        [fields.itemsList]: {
            fields: {
                title: string;
                imageUrl: string;
                link: LinkFieldValue;
            };
        }[];
        [fields.buttonList]: {
            fields: {
                buttonLabel: string;
                buttonVariant: ButtonVariants;
                buttonLink: LinkFieldValue;
            };
        }[];
    };
}

type Props = OwnProps & WidgetProps;

interface WhyKellerSupplyStyles {
    wrapper?: InjectableCss;
    infoSection?: InjectableCss;
    infoTitleSection?: InjectableCss;
    itemsSection?: InjectableCss;
    title?: TypographyPresentationProps;
    subtitle?: TypographyPresentationProps;
    buttonsSection?: InjectableCss;
    itemWrapper?: InjectableCss;
    item?: { clickable?: ClickablePresentationProps; title?: TypographyPresentationProps; image?: LazyImageProps };
}

const whyKellerSupplyStyles: WhyKellerSupplyStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: nowrap;
            padding: 20px 20px 12px;
            border: 1px solid ${color("common.border")};
            border-radius: 4px;
            background-color: ${color("common.white")};
            box-shadow: ${getProp("theme.shadows.4")};
            ${xScreen("lte", "md")} {
                flex-wrap: wrap;
            }
        `,
    },
    infoSection: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            flex-direction: column;
            max-width: 268px;
            width: 100%;
            margin-right: 60px;
            ${xScreen("lte", "md")} {
                margin-right: 0;
                margin-bottom: 30px;
                min-width: initial;
                flex-direction: row;
                max-width: initial;
                justify-content: space-between;
                flex-wrap: wrap;
            }
        `,
    },
    infoTitleSection: {
        css: css`
            margin-bottom: 24px;
            ${xScreen("lte", "md")} {
                margin-bottom: 0;
                min-width: 260px;
            }
            ${xScreen("lte", "xs")} {
                margin-bottom: 16px;
            }
        `,
    },
    title: {
        variant: "h4",
        forwardAs: "h2",
        css: css`
            margin-bottom: 15px;
        `,
    },
    subtitle: {
        variant: "h3",
        css: css`
            margin-bottom: 0;
        `,
    },
    buttonsSection: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;

            & > button:not(:last-child) {
                margin-right: 20px;
            }

            ${xScreen("lte", "md")} {
                margin-top: auto;
                margin-bottom: 10px;
            }
        `,
    },
    itemsSection: {
        css: css`
            flex-grow: 1;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            flex-wrap: nowrap;
            gap: 16px;
            ${xScreen("lte", "sm")} {
                flex-wrap: wrap;
            }
            ${xScreen("lte", "xs")} {
                flex-wrap: wrap;
                gap: 24px;
            }
        `,
    },
    itemWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex: 1 1 auto;
            flex-basis: 269px;
            min-width: 216px;
            border-top: 2px solid ${color("common.ui11")};
            padding-top: 24px;
            &:hover ${TypographyStyle} {
                color: ${color("common.ui1")};
            }

            ${xScreen("lte", "md")} {
                flex-basis: 216px;
            }
        `,
    },
    item: {
        title: {
            variant: "h5",
            css: css`
                &,
                ${TypographyStyle} {
                    margin-top: auto;
                    margin-bottom: auto;
                    font-size: 18px;
                    font-weight: bold;
                    letter-spacing: 0;
                    line-height: 22px;
                }
            `,
        },
        image: {
            css: css`
                flex-shrink: 0;
                margin-right: 20px;
                &,
                & img {
                    height: 68px;
                    width: 68px;
                }
            `,
        },
        clickable: {
            css: css`
                display: flex;
                align-items: flex-start;
            `,
        },
    },
};

const styles = whyKellerSupplyStyles;
const WhyKellerSupply: FC<Props> = ({ fields }) => {
    const { title, subtitle, buttonList } = fields;
    const { links: buttonLinks } = useGetLinks(fields.buttonList, o => o.fields.buttonLink);
    const { links: itemsLinks } = useGetLinks(fields.itemsList, o => o.fields.link);
    const history = useHistory();

    const onClick = (url?: string) => url && history.push(url);

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="whyKellerSupply">
            <StyledWrapper {...styles.infoSection}>
                <StyledWrapper {...styles.infoTitleSection}>
                    <Typography {...styles.title} data-test-selector="whyKellerSupply_title">
                        {title}
                    </Typography>
                    <Typography {...styles.subtitle} data-test-selector="whyKellerSupply_subtitle">
                        {subtitle}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.buttonsSection}>
                    {buttonList?.map((buttonItemField, index) => (
                        <Button
                            key={`${buttonItemField.fields.buttonLabel}_${index}`}
                            onClick={() => onClick(buttonLinks?.[index]?.url)}
                            variant={buttonItemField.fields.buttonVariant}
                        >
                            {buttonItemField.fields.buttonLabel}
                        </Button>
                    ))}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.itemsSection}>
                {fields.itemsList?.map((itemField, index) => {
                    const item = {
                        title: itemField.fields.title,
                        imageUrl: itemField.fields.imageUrl,
                        link: itemsLinks?.[index].url || "",
                    };

                    return (
                        <StyledWrapper {...styles.itemWrapper} key={`${item.title}_${index}`}>
                            <Clickable href={item.link} {...styles.item?.clickable}>
                                <LazyImage src={item.imageUrl} altText={item.title} {...styles.item?.image} />
                                <Typography {...styles.item?.title}>{parse(item.title, parserOptions)}</Typography>
                            </Clickable>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: WhyKellerSupply,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.subtitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.itemsList,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return xMask.removeTags(item.fields.title);
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "imageUrl",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Url", value: "" },
                        allowCategories: item => false,
                    },
                ],
            },
            {
                name: fields.buttonList,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.buttonLabel;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "buttonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "buttonVariant",
                        displayName: "Button Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
